import React from 'react';
import { Link } from 'react-router-dom';
import { getAppointmentStatusMeta } from '../../appointments/subparts/appointmentsQuery';

const DATE_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
];

function DashboardAppointments({
    dateFilter,
    onDateFilterChange,
    selectedEcpId,
    onEcpChange,
    ecpList,
    canFilterByEcp,
    getEcpDisplayName,
    appointments,
    isLoading,
    loadError,
    confirmedCount,
}) {
    return (
        <aside className="ecx-page-card ecx-page-panel dashboard-appointments dashboard-panel-theme dashboard-panel-theme--rose">
            <div className="ecx-page-panel__header dashboard-appointments__header">
                <div className="dashboard-appointments__title-row">
                    <div className="dashboard-appointments__heading">
                        <span className="ecx-page-icon-badge">
                            <i className="bi bi-calendar-check" />
                        </span>
                        <div className="min-width-0">
                            <h2 className="dashboard-appointments__title">Appointments</h2>
                            <p className="dashboard-appointments__subtitle mb-0">
                                {isLoading
                                    ? 'Loading schedule…'
                                    : `${appointments.length} total · ${confirmedCount} confirmed`}
                            </p>
                        </div>
                    </div>

                    {canFilterByEcp && (
                        <select
                            className="form-select form-select-sm dashboard-appointments__ecp-select"
                            value={selectedEcpId}
                            onChange={(event) => onEcpChange(event.target.value)}
                            disabled={isLoading}
                            aria-label="Filter by ECP"
                        >
                            <option value="">All ECPs</option>
                            {ecpList.map((ecp) => (
                                <option key={ecp.id} value={ecp.id}>
                                    {getEcpDisplayName(ecp)}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                <div className="dashboard-appointments__date-toggle" role="group" aria-label="Appointment date">
                    {DATE_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className={`dashboard-appointments__date-btn ${
                                dateFilter === option.value ? 'is-active' : ''
                            }`}
                            onClick={() => onDateFilterChange(option.value)}
                            disabled={isLoading}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="ecx-page-panel__body dashboard-appointments__body">
                {loadError && (
                    <div className="dashboard-alert" role="alert">
                        <i className="bi bi-exclamation-circle me-2" />
                        {loadError}
                    </div>
                )}

                {isLoading ? (
                    <div className="dashboard-appointments__loading">
                        {Array.from({ length: 6 }).map((_, index) => (
                            <div key={`appt-skel-${index}`} className="dashboard-appointments__skeleton-row" />
                        ))}
                    </div>
                ) : appointments.length === 0 ? (
                    <div className="ecx-page-empty py-4">
                        <span className="ecx-page-empty__icon">
                            <i className="bi bi-calendar-x" />
                        </span>
                        <h5 className="mb-2 text-dark">No appointments {dateFilter}</h5>
                        <p className="mb-0 text-muted">
                            {selectedEcpId
                                ? 'Try another ECP or switch between today and tomorrow.'
                                : 'Nothing scheduled for this day yet.'}
                        </p>
                    </div>
                ) : (
                    <div className="dashboard-appointments__list" role="list">
                        {appointments.map((appt) => {
                            const statusMeta = getAppointmentStatusMeta(appt.isConfirmed);
                            const displayName =
                                appt.patientName ||
                                `${appt.firstName || ''} ${appt.lastName || ''}`.trim() ||
                                'Unknown patient';

                            const doctorTooltip =
                                !selectedEcpId && appt.ecpName ? appt.ecpName : undefined;

                            return (
                                <div
                                    key={appt.id}
                                    className={`dashboard-appointments__row${appt.confirmed ? ' is-confirmed' : ''}`}
                                    role="listitem"
                                    title={doctorTooltip}
                                >
                                    <div className="dashboard-appointments__time">
                                        <span>{appt.startTime || '—'}</span>
                                    </div>

                                    <div className="dashboard-appointments__patient min-width-0">
                                        {appt.patientId ? (
                                            <Link
                                                to={`/patient-files/${appt.patientId}`}
                                                className="dashboard-appointments__name"
                                            >
                                                {displayName}
                                            </Link>
                                        ) : (
                                            <span className="dashboard-appointments__name">{displayName}</span>
                                        )}
                                    </div>

                                    <span
                                        className={`dashboard-appointments__status dashboard-appointments__status--${statusMeta.variant}`}
                                        title={statusMeta.label}
                                    >
                                        <i className={`bi ${statusMeta.icon}`} aria-hidden="true" />
                                        <span className="dashboard-appointments__status-label">
                                            {statusMeta.label}
                                        </span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </aside>
    );
}

export default DashboardAppointments;
